import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Colors as colors } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { subscribeNoticiasNewsletter } from '../../lib/noticiasNewsletterService.js';
import { dreamvinylOfficialSocialUrl, DREAMVINYL_SOCIAL_LABELS } from '../constants/dreamvinylSocial.js';
import { NOTICIA_CONTENIDO_PURIFY } from '../../lib/sanitizeArticleHtml.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import AdSlot from '../components/AdSlot.jsx';
import { fetchAdBySlot, trackAdClick, trackAdImpression } from '../../lib/adsService.js';
import { buildPublicContentUrl } from '../../lib/socialPublishKit.js';
import { setPageMeta, resetPageMeta } from '../../lib/pageMeta.js';
import { useAppDataLoad } from '../hooks/useAppDataLoad.js';
import NoticiaImagenCredit from '../components/NoticiaImagenCredit.jsx';

const CATS = ['Todo', 'Vinilo', 'Mercado', 'Eventos', 'Subastas', 'Comunidad'];

const CAT_ACCENTS = {
  Vinilo: '#c8a84b',
  Mercado: '#5DCAA5',
  Eventos: '#a89cf7',
  Subastas: '#e08060',
  Comunidad: '#5b9bd5',
};

const HERO_BG = 'linear-gradient(135deg,#1a1a2e,#0d1020)';
const HEART_LIKED = '#dc3545';

/** Invitados: mismo origen que el feed de me gustas de noticias (card + modal). */
const LS_NOTICIA_LIKES_KEY = 'dreamvinyl_noticias_megusta_v1';

function readLocalNoticiaMegustaIds() {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(LS_NOTICIA_LIKES_KEY);
    const arr = JSON.parse(raw || '[]');
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  } catch (_) {
    return [];
  }
}

function writeLocalNoticiaMegustaIds(ids) {
  try {
    localStorage.setItem(LS_NOTICIA_LIKES_KEY, JSON.stringify([...new Set(ids)]));
  } catch (_) {
    /* noop */
  }
}

function formatFechaCorta(iso) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch (_) {
    return '';
  }
}

export default function PageNoticias({
  onBack,
  onNavigate,
  onShowModal,
  user,
  deepLinkNoticiaId,
  onConsumedDeepLinkNoticia,
}) {
  const [catSel, setCatSel] = useState('Todo');
  const [sponsoredAd, setSponsoredAd] = useState(null);
  const [sponsoredLoaded, setSponsoredLoaded] = useState(false);

  const fetchNoticias = useCallback(async () => {
    const { data, error } = await supabase
      .from('noticias_musica')
      .select('*')
      .eq('publicado', true)
      .order('fecha_publicacion', { ascending: false });
    if (error) throw new Error(error.message || 'No se pudieron cargar las noticias');
    return data ?? [];
  }, []);

  const {
    data: noticiasRaw,
    loading,
    error: loadError,
    reload: reloadNoticias,
  } = useAppDataLoad(fetchNoticias, []);
  const noticias = useMemo(() => noticiasRaw ?? [], [noticiasRaw]);
  const [openArticle, setOpenArticle] = useState(null);
  const [featuredHeroIdx, setFeaturedHeroIdx] = useState(0);
  const [heroFeaturedOpaque, setHeroFeaturedOpaque] = useState(true);
  const [likedMap, setLikedMap] = useState({});
  const [actionToast, setActionToast] = useState('');
  const actionToastTimerRef = useRef(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterBusy, setNewsletterBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSponsoredLoaded(false);
    fetchAdBySlot('noticia_patrocinada').then(ad => {
      if (cancelled) return;
      setSponsoredAd(ad || null);
      setSponsoredLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sponsoredAd?.id) return;
    trackAdImpression(sponsoredAd.id);
  }, [sponsoredAd?.id]);

  useEffect(() => {
    if (!openArticle) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') setOpenArticle(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openArticle]);

  useEffect(() => {
    if (!deepLinkNoticiaId) return;
    if (loading) return;
    const hit = noticias.find(n => n.id === deepLinkNoticiaId);
    if (hit) setOpenArticle(hit);
    onConsumedDeepLinkNoticia?.();
  }, [deepLinkNoticiaId, loading, noticias, onConsumedDeepLinkNoticia]);

  useEffect(() => {
    if (openArticle?.titulo) {
      const desc =
        openArticle.extracto?.trim() ||
        stripHtmlBrief(openArticle.contenido_html) ||
        `Noticia de ${openArticle.categoria || 'DreamVinyl'}.`;
      setPageMeta({ title: `DreamVinyl · ${openArticle.titulo}`, description: desc });
    } else {
      setPageMeta({
        title: 'DreamVinyl · Noticias',
        description: 'Noticias de vinilo, mercado, eventos y comunidad.',
      });
    }
    return () => resetPageMeta();
  }, [openArticle]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        const ids = readLocalNoticiaMegustaIds();
        const map = {};
        ids.forEach(id => {
          map[id] = true;
        });
        if (!cancelled) setLikedMap(map);
        return;
      }

      const guestIds = readLocalNoticiaMegustaIds();
      const { data, error } = await supabase.from('noticia_megusta').select('noticia_id').eq('user_id', user.id);
      if (cancelled) return;

      const map = {};
      (data || []).forEach(r => {
        if (r?.noticia_id) map[r.noticia_id] = true;
      });

      if (!error && guestIds.length) {
        const existing = new Set(Object.keys(map));
        const toInsert = guestIds.filter(id => !existing.has(id)).map(noticia_id => ({ user_id: user.id, noticia_id }));
        if (toInsert.length) {
          await supabase.from('noticia_megusta').insert(toInsert);
          toInsert.forEach(r => {
            map[r.noticia_id] = true;
          });
        }
        try {
          localStorage.removeItem(LS_NOTICIA_LIKES_KEY);
        } catch (_) {
          /* noop */
        }
      }

      if (!cancelled) setLikedMap(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const showActionToast = useCallback((msg, durationMs = 2200) => {
    setActionToast(msg);
    if (actionToastTimerRef.current) clearTimeout(actionToastTimerRef.current);
    actionToastTimerRef.current = setTimeout(() => setActionToast(''), durationMs);
  }, []);

  const handleNoticiasNewsletterSubscribe = useCallback(async () => {
    const raw = newsletterEmail.trim();
    if (!raw) {
      showActionToast('Escribe tu correo electrónico.', 2800);
      return;
    }
    if (raw.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      showActionToast('Revisa que el correo sea válido.', 3200);
      return;
    }
    setNewsletterBusy(true);
    try {
      const result = await subscribeNoticiasNewsletter(raw);
      if (!result.ok) {
        showActionToast(result.error || 'No se pudo guardar. Inténtalo de nuevo.', 3800);
        return;
      }
      setNewsletterEmail('');
      if (result.alreadySubscribed) {
        showActionToast('Ese correo ya estaba en la lista. ¡Gracias!', 4000);
        return;
      }
      if (result.emailSent) {
        showActionToast('¡Listo! Revisa tu correo: te hemos enviado un mensaje de bienvenida.', 4800);
      } else {
        showActionToast('¡Te hemos apuntado! Te avisaremos cuando publiquemos novedades.', 4200);
      }
    } finally {
      setNewsletterBusy(false);
    }
  }, [newsletterEmail, showActionToast]);

  const socialLinksForNewsletter = useMemo(() => {
    return ['telegram', 'instagram', 'tiktok', 'youtube']
      .map(key => {
        const url = dreamvinylOfficialSocialUrl(key);
        if (!url) return null;
        return { key, url, label: DREAMVINYL_SOCIAL_LABELS[key] || key };
      })
      .filter(Boolean);
  }, []);

  const toggleLikeNoticia = useCallback(
    async (noticiaId, e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      let targetLiked = false;
      setLikedMap(prev => {
        const on = !!prev[noticiaId];
        targetLiked = !on;
        const copy = { ...prev };
        if (targetLiked) copy[noticiaId] = true;
        else delete copy[noticiaId];
        return copy;
      });

      if (!user?.id) {
        const set = new Set(readLocalNoticiaMegustaIds());
        if (targetLiked) set.add(noticiaId);
        else set.delete(noticiaId);
        writeLocalNoticiaMegustaIds([...set]);
        return;
      }

      if (targetLiked) {
        const { error } = await supabase.from('noticia_megusta').insert({ user_id: user.id, noticia_id: noticiaId });
        if (error) {
          setLikedMap(prev => {
            const copy = { ...prev };
            delete copy[noticiaId];
            return copy;
          });
          return;
        }
      } else {
        const { error } = await supabase.from('noticia_megusta').delete().eq('user_id', user.id).eq('noticia_id', noticiaId);
        if (error) {
          setLikedMap(prev => ({ ...prev, [noticiaId]: true }));
          return;
        }
      }
    },
    [user?.id]
  );

  const shareNoticia = useCallback(
    async (articulo, e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const id = articulo?.id;
      if (!id) return;
      const url = buildPublicContentUrl('noticia', id);
      const title = articulo?.titulo ? `DreamVinyl · ${articulo.titulo}` : 'DreamVinyl · Noticia';
      const text =
        articulo?.extracto?.trim() ||
        stripHtmlBrief(articulo?.contenido_html) ||
        title;
      try {
        if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
          await navigator.share({ title, text, url });
          showActionToast('Compartido');
          return;
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
      try {
        await navigator.clipboard.writeText(url);
        showActionToast('Enlace copiado');
      } catch (_) {
        showActionToast('Copia este enlace: ' + url);
      }
    },
    [showActionToast]
  );

  useEffect(() => {
    return () => {
      if (actionToastTimerRef.current) clearTimeout(actionToastTimerRef.current);
    };
  }, []);

  const btnCardAction = useMemo(
    () => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '4px 10px',
      borderRadius: 8,
      border: '1px solid rgba(0,0,0,0.1)',
      background: '#f7f7f9',
      fontSize: 11,
      cursor: 'pointer',
      fontFamily: 'inherit',
      color: '#555',
      fontWeight: 500,
    }),
    []
  );

  const btnModalAction = useMemo(
    () => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 12px',
      borderRadius: 10,
      border: '1px solid rgba(0,0,0,0.1)',
      background: '#fafafa',
      fontSize: 12,
      cursor: 'pointer',
      fontFamily: 'inherit',
      color: '#444',
      fontWeight: 600,
    }),
    []
  );

  const rowsUi = useMemo(
    () =>
      (noticias || []).map(n => ({
        raw: n,
        id: n.id,
        cat: n.categoria,
        titulo: n.titulo,
        extracto: n.extracto?.trim()
          ? n.extracto
          : stripHtmlBrief(n.contenido_html),
        fecha: formatFechaCorta(n.fecha_publicacion || n.created_at),
        tiempo: n.tiempo_lectura?.trim() ? n.tiempo_lectura : '',
        imgUrl: n.imagen_url?.trim() || null,
        accentColor: CAT_ACCENTS[n.categoria] || '#c8a84b',
        destacado: !!n.destacado,
      })),
    [noticias]
  );

  const filtered = useMemo(() => {
    const base = rowsUi.filter(n => !!n.titulo);
    const list = catSel === 'Todo' ? base : base.filter(n => n.cat === catSel);
    return [...list].sort((a, b) => {
      const ta = new Date(a.raw.fecha_publicacion || a.raw.created_at || 0).getTime();
      const tb = new Date(b.raw.fecha_publicacion || b.raw.created_at || 0).getTime();
      return tb - ta;
    });
  }, [rowsUi, catSel]);

  const heroPool = useMemo(() => {
    const list = rowsUi.filter(n => n.destacado);
    const byDate = [...list].sort((a, b) => {
      const ta = new Date(a.raw.fecha_publicacion || a.raw.created_at || 0).getTime();
      const tb = new Date(b.raw.fecha_publicacion || b.raw.created_at || 0).getTime();
      return tb - ta;
    });
    return byDate;
  }, [rowsUi]);

  const heroPoolKey = useMemo(() => heroPool.map(h => h.id).join('|'), [heroPool]);

  useEffect(() => {
    setFeaturedHeroIdx(0);
    setHeroFeaturedOpaque(true);
  }, [heroPoolKey]);

  useEffect(() => {
    if (heroPool.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setHeroFeaturedOpaque(false);
    }, 6000);
    return () => window.clearInterval(id);
  }, [heroPool.length, heroPoolKey]);

  const slidingFeaturedHero = heroPool.length > 1;
  const heroSlide =
    heroPool.length > 0 ? heroPool[featuredHeroIdx % heroPool.length] : null;
  const hero = heroSlide || filtered[0] || null;
  const heroBlockOpacity = slidingFeaturedHero ? (heroFeaturedOpaque ? 1 : 0) : 1;

  const safeArticleHtml = useMemo(
    () => DOMPurify.sanitize(String(openArticle?.contenido_html ?? ''), NOTICIA_CONTENIDO_PURIFY),
    [openArticle?.contenido_html]
  );

  return (
    <div style={{ background: colors.cream, minHeight: '100vh' }}>
      <WebTopBar
        navActive="noticias"
        dark
        user={user}
        onNavigate={onNavigate}
        onShowModal={onShowModal}
        onSignOut={onBack}
      />

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 20px 60px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 200px', gap: 18, alignItems: 'start' }}>
          <main style={{ minWidth: 0 }}>
            <div
              role="presentation"
              onClick={() => hero && setOpenArticle(hero.raw)}
              style={{
                borderRadius: 16,
                overflow: 'hidden',
                marginBottom: 28,
                cursor: hero ? 'pointer' : 'default',
                position: 'relative',
                height: 280,
                background: HERO_BG,
                display: 'flex',
                alignItems: 'flex-end',
                opacity: loading ? 0.85 : heroBlockOpacity,
                transition: slidingFeaturedHero ? 'opacity 0.45s ease-in-out' : undefined,
              }}
              onTransitionEnd={e => {
                if (e.propertyName !== 'opacity') return;
                if (!slidingFeaturedHero || heroFeaturedOpaque) return;
                setFeaturedHeroIdx(i => {
                  const n = heroPool.length;
                  return n <= 1 ? 0 : (i + 1) % n;
                });
                requestAnimationFrame(() => setHeroFeaturedOpaque(true));
              }}
            >
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 50%, rgba(200,168,75,0.25) 0%, transparent 60%)' }} />
              {hero?.imgUrl ? (
                <img
                  src={hero.imgUrl}
                  alt=""
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.45 }}
                />
              ) : hero ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(ellipse at 20% 60%, ${hero.accentColor}55 0%, transparent 55%)`,
                  }}
                />
              ) : null}
              <div style={{ position: 'relative', padding: 28 }}>
                <span
                  style={{
                    background: colors.gold,
                    color: '#000',
                    padding: '3px 9px',
                    borderRadius: 3,
                    fontSize: '0.65rem',
                    fontWeight: 900,
                    letterSpacing: '0.1em',
                    marginBottom: 10,
                    display: 'inline-block',
                  }}
                >
                  DESTACADO
                </span>
                {loadError ? (
                  <>
                    <h1 style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700, margin: '8px 0 6px', maxWidth: 600, lineHeight: 1.3 }}>
                      No se pudieron cargar las noticias
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.88rem', marginBottom: 8, maxWidth: 500 }}>{loadError}</p>
                    <button
                      type="button"
                      onClick={() => reloadNoticias()}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.gold,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: '0.88rem',
                      }}
                    >
                      Reintentar
                    </button>
                  </>
                ) : loading ? (
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem' }}>Cargando noticias…</p>
                ) : hero ? (
                  <>
                    <h1
                      style={{
                        color: '#fff',
                        fontSize: '1.7rem',
                        fontWeight: 700,
                        margin: '8px 0 6px',
                        fontFamily: 'Georgia,serif',
                        maxWidth: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {hero.titulo}
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.88rem', marginBottom: 8, maxWidth: 500 }}>{hero.extracto}</p>
                    <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' }}>
                      {hero.fecha}
                      {hero.tiempo ? ` · ${hero.tiempo}` : ''}
                    </span>
                  </>
                ) : (
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem' }}>Pronto publicaremos novedades musicales.</p>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {CATS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCatSel(c)}
                  style={{
                    padding: '6px 16px',
                    borderRadius: 20,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    border: `1px solid ${catSel === c ? colors.gold : 'rgba(0,0,0,0.15)'}`,
                    background: catSel === c ? colors.gold : '#fff',
                    color: catSel === c ? '#000' : '#555',
                    fontWeight: catSel === c ? 700 : 400,
                    transition: 'all .15s',
                  }}
                >
                  {c}
                </button>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 18 }}>
              {sponsoredLoaded && sponsoredAd ? (
                <div
                  key={`ad-${sponsoredAd.id}`}
                  style={{
                    background: '#fff',
                    borderRadius: 13,
                    overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.07)',
                    cursor: 'pointer',
                    transition: 'all .2s',
                  }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    trackAdClick(sponsoredAd.id);
                    window.open(sponsoredAd.target_url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <div style={{ height: 140, position: 'relative', overflow: 'hidden', background: '#0a0a0f' }}>
                    {String(sponsoredAd.media_type || '').toLowerCase() === 'video' ? (
                      <video
                        src={sponsoredAd.media_url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <img
                        src={sponsoredAd.media_url}
                        alt={sponsoredAd.alt_text || ''}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    )}
                    <span
                      style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        background: colors.gold,
                        color: '#000',
                        padding: '2px 8px',
                        borderRadius: 3,
                        fontSize: '0.62rem',
                        fontWeight: 900,
                        letterSpacing: '0.08em',
                      }}
                    >
                      PATROCINADO
                    </span>
                  </div>
                </div>
              ) : null}
              {filtered.map(n => (
                <div
                  key={n.id}
                  role="presentation"
                  onClick={() => setOpenArticle(n.raw)}
                  style={{
                    background: '#fff',
                    borderRadius: 13,
                    overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.07)',
                    cursor: 'pointer',
                    transition: 'all .2s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ height: 140, background: n.imgUrl ? '#111' : '#1a1a2e', position: 'relative', overflow: 'hidden' }}>
                    {n.imgUrl ? (
                      <img src={n.imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <>
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: `radial-gradient(ellipse at 20% 60%, ${n.accentColor}33 0%, transparent 60%)`,
                          }}
                        />
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: '50%',
                              background: 'rgba(0,0,0,0.4)',
                              border: `2px solid ${n.accentColor}66`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Icon name="disc" size={24} color={n.accentColor} />
                          </div>
                        </div>
                      </>
                    )}
                    <span
                      style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        background: n.accentColor,
                        color: '#000',
                        padding: '2px 8px',
                        borderRadius: 3,
                        fontSize: '0.62rem',
                        fontWeight: 900,
                        letterSpacing: '0.08em',
                      }}
                    >
                      {(n.cat || '').toUpperCase()}
                    </span>
                  </div>
                  <div style={{ padding: '14px 16px 16px' }}>
                    <h3
                      style={{
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#1a1a1a',
                        lineHeight: 1.4,
                        marginBottom: 7,
                        fontFamily: 'Georgia,serif',
                      }}
                    >
                      {n.titulo}
                    </h3>
                    <p style={{ fontSize: '0.78rem', color: '#888', lineHeight: 1.6, marginBottom: 10 }}>{n.extracto}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.7rem', color: '#bbb' }}>{n.fecha}</span>
                      <span style={{ fontSize: '0.7rem', color: colors.gold, fontWeight: 600 }}>{n.tiempo ? `${n.tiempo} →` : '→'}</span>
                    </div>
                    <div
                      role="presentation"
                      onClick={ev => ev.stopPropagation()}
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}
                    >
                      <button
                        type="button"
                        style={btnCardAction}
                        onClick={e => toggleLikeNoticia(n.id, e)}
                        aria-pressed={!!likedMap[n.id]}
                        aria-label={likedMap[n.id] ? 'Quitar me gusta' : 'Me gusta'}
                      >
                        <Icon name={likedMap[n.id] ? 'heartSolid' : 'heart'} size={14} color={likedMap[n.id] ? HEART_LIKED : '#aaa'} />
                        Me gusta
                      </button>
                      <button
                        type="button"
                        style={btnCardAction}
                        onClick={e => shareNoticia(n.raw, e)}
                        aria-label="Compartir noticia"
                      >
                        <Icon name="share" size={14} color="#888" />
                        Compartir
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!loading && !filtered.length ? (
              <p style={{ textAlign: 'center', marginTop: 24, color: '#888', fontSize: 14 }}>No hay noticias en esta categoría por ahora.</p>
            ) : null}

            <div
              style={{
                marginTop: 40,
                background: 'linear-gradient(135deg,#1a1a2e,#0d1020)',
                borderRadius: 16,
                padding: '28px 32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 20,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <h3 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700, marginBottom: 5 }}>Recibe las últimas novedades</h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', lineHeight: 1.5, maxWidth: 420 }}>
                  Te avisaremos por correo con noticias que publiquemos en la web y, de vez en cuando, publicaciones
                  destacadas de la comunidad. Al suscribirte recibirás un email de bienvenida.
                </p>
                {socialLinksForNewsletter.length > 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.78rem', marginTop: 8, lineHeight: 1.45 }}>
                    Para estar aún más al día, síguenos también en{' '}
                    {socialLinksForNewsletter.map((s, i) => (
                      <span key={s.key}>
                        {i > 0 ? (i === socialLinksForNewsletter.length - 1 ? ' y ' : ', ') : ''}
                        <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: colors.gold }}>
                          {s.label.replace(' DreamVinyl', '')}
                        </a>
                      </span>
                    ))}
                    .
                  </p>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'stretch' }}>
                <input
                  type="email"
                  name="noticias-newsletter-email"
                  autoComplete="email"
                  placeholder="tu@email.com"
                  value={newsletterEmail}
                  onChange={e => setNewsletterEmail(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!newsletterBusy) handleNoticiasNewsletterSubscribe();
                    }
                  }}
                  disabled={newsletterBusy}
                  aria-busy={newsletterBusy}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.08)',
                    color: '#fff',
                    fontSize: '0.84rem',
                    outline: 'none',
                    width: 220,
                    fontFamily: 'inherit',
                    opacity: newsletterBusy ? 0.7 : 1,
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = colors.gold;
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = 'rgba(255,255,255,0.15)';
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleNoticiasNewsletterSubscribe()}
                  disabled={newsletterBusy}
                  aria-busy={newsletterBusy}
                  style={{
                    padding: '10px 18px',
                    borderRadius: 8,
                    background: colors.gold,
                    color: '#000',
                    border: 'none',
                    fontSize: '0.84rem',
                    fontWeight: 700,
                    cursor: newsletterBusy ? 'wait' : 'pointer',
                    whiteSpace: 'nowrap',
                    opacity: newsletterBusy ? 0.75 : 1,
                  }}
                >
                  {newsletterBusy ? 'Guardando…' : 'Suscribirme'}
                </button>
              </div>
            </div>
          </main>

          <aside
            style={{
              position: 'sticky',
              top: 70,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              alignSelf: 'start',
              height: 'calc(100vh - 90px)',
            }}
          >
            <div
              style={{
                borderRadius: 12,
                border: 'none',
                background: 'transparent',
                padding: 0,
                margin: 0,
                overflow: 'hidden',
                textAlign: 'center',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'stretch',
              }}
            >
              <AdSlot slot="lateral_noticias" />
            </div>
          </aside>
        </div>
      </div>

      {openArticle ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Artículo"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'rgba(10,10,15,0.72)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '24px 16px',
            overflowY: 'auto',
          }}
          onClick={() => setOpenArticle(null)}
        >
          <div
            role="presentation"
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 680,
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 22px 60px rgba(0,0,0,0.35)',
              marginTop: '4vh',
              marginBottom: 40,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: colors.gold, letterSpacing: '0.1em', marginBottom: 6 }}>
                  {(openArticle.categoria || '').toUpperCase()}
                </div>
                <h2 style={{ fontSize: '1.35rem', fontWeight: 750, margin: 0, fontFamily: 'Georgia,serif', lineHeight: 1.35, color: '#111' }}>
                  {openArticle.titulo}
                </h2>
                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                  {formatFechaCorta(openArticle.fecha_publicacion || openArticle.created_at)}
                  {openArticle.tiempo_lectura?.trim() ? ` · ${openArticle.tiempo_lectura}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginLeft: 'auto' }}>
                <button
                  type="button"
                  style={btnModalAction}
                  onClick={e => toggleLikeNoticia(openArticle.id, e)}
                  aria-pressed={!!likedMap[openArticle.id]}
                  aria-label={likedMap[openArticle.id] ? 'Quitar me gusta' : 'Me gusta'}
                >
                  <Icon name={likedMap[openArticle.id] ? 'heartSolid' : 'heart'} size={16} color={likedMap[openArticle.id] ? HEART_LIKED : '#999'} />
                  Me gusta
                </button>
                <button type="button" style={btnModalAction} onClick={e => shareNoticia(openArticle, e)} aria-label="Compartir noticia">
                  <Icon name="share" size={16} color="#888" />
                  Compartir
                </button>
                <button
                  type="button"
                  onClick={() => setOpenArticle(null)}
                  aria-label="Cerrar"
                  style={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.1)',
                    background: '#fafafa',
                    cursor: 'pointer',
                    fontSize: 18,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            {openArticle.imagen_url ? (
              <div style={{ background: '#0a0a0f' }}>
                <div style={{ maxHeight: 280, overflow: 'hidden' }}>
                  <img src={openArticle.imagen_url} alt="" style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 280 }} />
                </div>
                <NoticiaImagenCredit noticia={openArticle} />
              </div>
            ) : null}
            <div
              style={{
                padding: '20px 22px 26px',
                fontSize: 15,
                lineHeight: 1.7,
                color: '#333',
              }}
              className="dv-noticia-body"
            >
              {safeArticleHtml ? <div dangerouslySetInnerHTML={{ __html: safeArticleHtml }} /> : <p>{stripHtmlBrief(openArticle.contenido_html) || 'Sin contenido.'}</p>}
            </div>
          </div>
        </div>
      ) : null}

      {actionToast ? (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 88,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2100,
            maxWidth: 'min(440px, 92vw)',
            padding: '10px 16px',
            borderRadius: 12,
            background: 'rgba(26,26,46,0.92)',
            color: '#fff',
            fontSize: 13,
            boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          {actionToast}
        </div>
      ) : null}
    </div>
  );
}

function stripHtmlBrief(html) {
  const t = String(html ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return t.length > 220 ? `${t.slice(0, 217)}…` : t;
}
